import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import AdminOverviewTab from "@/components/admin/AdminOverviewTab";
import AdminMembersTab from "@/components/admin/AdminMembersTab";
import AdminCoachesTab from "@/components/admin/AdminCoachesTab";
import AdminIntegrationsTab from "@/components/admin/AdminIntegrationsTab";
import AdminMediaTab from "@/components/admin/AdminMediaTab";
import AdminSettingsTab from "@/components/admin/AdminSettingsTab";
import ClientsTab from "@/components/coach/ClientsTab";
import GroupsTab from "@/components/coach/GroupsTab";
import PlannerTab from "@/components/coach/PlannerTab";
import LibraryTab from "@/components/coach/LibraryTab";
import SessionsTab from "@/components/coach/SessionsTab";
import FeedbackTab from "@/components/coach/FeedbackTab";
import PaymentsTab from "@/components/coach/PaymentsTab";
import TestimonialsTab from "@/components/coach/TestimonialsTab";

const TABS = [
  "Overview", "Clients", "Members", "Coaches", "Groups", "Planner", "Library",
  "Sessions", "Feedback", "Payments", "Testimonials", "Media", "Integrations", "Settings",
];

export default function Admin() {
  const [tab, setTab] = useState("Overview");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70 sticky top-0 bg-ink/90 backdrop-blur-md z-40">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" data-testid="admin-home-link"><Wordmark className="text-2xl" /></Link>
          <span className="text-[9px] uppercase tracking-[0.25em] font-extrabold text-black bg-volt px-2 py-1">Admin</span>
          <span className="hidden md:inline text-[10px] text-zinc-500 truncate">{user?.email}</span>
        </div>
        <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/admin-login"); }}
          className="text-zinc-400 hover:text-volt transition-colors" aria-label="Sign out">
          <LogOut size={18} />
        </button>
      </header>

      <nav className="flex gap-1 overflow-x-auto no-scrollbar px-5 sm:px-10 border-b border-zinc-800/70 sticky top-16 bg-ink/90 backdrop-blur-md z-30">
        {TABS.map((t) => (
          <button key={t} data-testid={`admin-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-4 text-xs uppercase tracking-[0.16em] font-bold whitespace-nowrap border-b-2 transition-colors ${
              tab === t ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-white"}`}>
            {t}
          </button>
        ))}
      </nav>

      <main className="px-5 sm:px-10 lg:px-16 py-10 max-w-7xl mx-auto">
        {tab === "Overview" && <AdminOverviewTab />}
        {tab === "Clients" && <ClientsTab />}
        {tab === "Members" && <AdminMembersTab />}
        {tab === "Coaches" && <AdminCoachesTab />}
        {tab === "Groups" && <GroupsTab />}
        {tab === "Planner" && <PlannerTab />}
        {tab === "Library" && <LibraryTab />}
        {tab === "Sessions" && <SessionsTab />}
        {tab === "Feedback" && <FeedbackTab />}
        {tab === "Payments" && <PaymentsTab />}
        {tab === "Testimonials" && <TestimonialsTab />}
        {tab === "Media" && <AdminMediaTab />}
        {tab === "Integrations" && <AdminIntegrationsTab />}
        {tab === "Settings" && <AdminSettingsTab />}
      </main>
    </div>
  );
}
